/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { SortDirection } from "@atg/icf-react-components/utils/sortTable";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import cloneDeep from "lodash/fp/cloneDeep";
import merge from "lodash/fp/merge";

import { PayProgramType, RequestStatus } from "~/constants";
import {
  JoinType,
  SKIN_REVIEW_STATUS_APPROVED,
  SKIN_REVIEW_STATUS_PENDING,
  SkinReviewStatusNameMap,
  SubdomainEnding,
  WizardAction,
} from "~/features/mySites/constants";
import {
  convertWhitelabelLogoAsync,
  getPostbackListAsync,
  getPostbackSitesAsync,
  getTimezonesAsync,
  loadMySitesAsync,
  loadPromotionSitesAsync,
  postAddPostbackAsync,
  postCreateBlackLabelSiteReviewAsync,
  postCreateNewSiteDraftAsync,
  postCreatePromotionAsync,
  postDeletePostbackAsync,
  postEditPostbackAsync,
  postValidateWhitelabelDomainAsync,
} from "~/features/mySites/thunks";
import { MySitesState } from "~/features/mySites/types";
import { PaginationSettings } from "~/types";

export const initialState: MySitesState = {
  manageSites: {
    meta: {
      loadStatus: RequestStatus.IDLE,
    },
    data: {
      totalSites: 0,
      siteList: [],
    },
  },
  createSite: {
    meta: {
      domainValidationStatus: RequestStatus.IDLE,
      uploadLogoStatus: RequestStatus.IDLE,
      convertLogoStatus: RequestStatus.IDLE,
      generateLogoStatus: RequestStatus.IDLE,
      updateJoinTypeStatus: RequestStatus.IDLE,
      siteCreationStatus: RequestStatus.IDLE,
    },
    data: {
      domain: "",
      subDomain: "",
      subDomainEnding: SubdomainEnding.ALL,
      domainType: WizardAction.DOMAIN,
      payProgram: PayProgramType["35LRS"],
      joinType: JoinType.PREMIUM,
      logoUrl: "",
      logoData: "",
      validatedDomain: "",
      newReviewSiteId: 0,
    },
  },
  promoteSite: {
    meta: {
      loadStatus: RequestStatus.IDLE,
      createStatus: RequestStatus.IDLE,
    },
    data: {
      promotedPrograms: {},
      selectedSiteId: 0,
      selectedProgramId: 0,
    },
  },
  postback: {
    meta: {
      getPostbacksStatus: RequestStatus.IDLE,
      getSitesStatus: RequestStatus.IDLE,
      addPostbackStatus: RequestStatus.IDLE,
      editPostbackStatus: RequestStatus.IDLE,
      deletePostbackStatus: RequestStatus.IDLE,
    },
    data: {
      postbacks: [],
      sites: [],
      promotions: [],
      totalPages: 0,
      firstLoad: true,
      pagination: {
        currentPageIndex: 0,
        sortedColumn: "postbackid",
        sortDirection: SortDirection.ASCEND,
        initialized: false,
      },
    },
  },
  createBlackLabel: {
    meta: {
      saveStatus: RequestStatus.IDLE,
    },
  },
  timezones: {
    meta: {
      getStatus: RequestStatus.IDLE,
    },
    data: {
      result: [],
    },
  },
};

const slice = createSlice({
  name: "mySites",
  initialState,
  reducers: {
    // MY SITES
    resetMySitesState: (state) => {
      state.manageSites = cloneDeep(initialState.manageSites);
    },

    setSiteStatus: (state, action: PayloadAction<string>) => {
      const list = state.manageSites.data.siteList;
      const selectedSite = list.findIndex((site) => site.domain === action.payload);
      if (selectedSite && list[selectedSite].statusId !== SKIN_REVIEW_STATUS_APPROVED) {
        list[selectedSite].statusId = SKIN_REVIEW_STATUS_PENDING;
        list[selectedSite].statusName = SkinReviewStatusNameMap[SKIN_REVIEW_STATUS_PENDING];
        list[selectedSite].isEditable = false;
      }

      state.manageSites.data.siteList = list;
    },
    // CREATE WHITE LABEL
    resetCreateSiteData: (state) => {
      state.createSite = cloneDeep(initialState.createSite);
    },
    // PROMOTE SITE
    updateSelectedSiteId: (state, action: PayloadAction<number>) => {
      state.promoteSite.data.selectedSiteId = action.payload;
    },

    resetPromoteSiteState: (state) => {
      state.promoteSite = cloneDeep(initialState.promoteSite);
    },

    // POSTBACK
    setPagination: (state, action: PayloadAction<Partial<PaginationSettings>>) => {
      state.postback.data.pagination = merge(state.postback.data.pagination, action.payload);
    },

    resetPostbackState: (state) => {
      state.postback = cloneDeep(initialState.postback);
    },

    // BLACK LABEL
    resetCreateBlackLabelState: (state) => {
      state.createBlackLabel = cloneDeep(initialState.createBlackLabel);
    },

    getTimezones: (state) => {
      state.timezones = cloneDeep(initialState.timezones);
    },
  },
  extraReducers: (builder) => {
    builder
      // MY SITES
      .addCase(loadMySitesAsync.pending, (state) => {
        state.manageSites.meta.loadStatus = RequestStatus.PENDING;
      })

      .addCase(loadMySitesAsync.fulfilled, (state, action) => {
        state.manageSites.meta.loadStatus = RequestStatus.SUCCESS;
        state.manageSites.data = action.payload;
      })

      .addCase(loadMySitesAsync.rejected, (state) => {
        state.manageSites.meta.loadStatus = RequestStatus.ERROR;
      })

      // CREATE WHITE LABEL
      .addCase(postValidateWhitelabelDomainAsync.pending, (state) => {
        const { createSite } = state;
        createSite.meta.domainValidationStatus = RequestStatus.PENDING;
      })

      .addCase(postValidateWhitelabelDomainAsync.fulfilled, (state, action) => {
        const { createSite } = state;
        createSite.meta.domainValidationStatus = RequestStatus.SUCCESS;
        createSite.data = merge(createSite.data, action.payload);
      })

      .addCase(postValidateWhitelabelDomainAsync.rejected, (state) => {
        const { createSite } = state;
        createSite.meta.domainValidationStatus = RequestStatus.ERROR;
      })

      .addCase(postCreateNewSiteDraftAsync.pending, (state) => {
        const { createSite } = state;
        createSite.meta.siteCreationStatus = RequestStatus.PENDING;
      })

      .addCase(postCreateNewSiteDraftAsync.fulfilled, (state, action) => {
        const { createSite } = state;
        createSite.meta.siteCreationStatus = RequestStatus.SUCCESS;
        createSite.data = merge(createSite.data, action.payload);
      })

      .addCase(postCreateNewSiteDraftAsync.rejected, (state) => {
        const { createSite } = state;
        createSite.meta.siteCreationStatus = RequestStatus.ERROR;
      })

      .addCase(convertWhitelabelLogoAsync.pending, (state) => {
        const { createSite } = state;
        createSite.meta.convertLogoStatus = RequestStatus.PENDING;
      })

      .addCase(convertWhitelabelLogoAsync.fulfilled, (state, action) => {
        const { createSite } = state;
        createSite.meta.convertLogoStatus = RequestStatus.SUCCESS;
        createSite.data.logoData = action.payload;
      })

      .addCase(convertWhitelabelLogoAsync.rejected, (state) => {
        const { createSite } = state;
        createSite.meta.convertLogoStatus = RequestStatus.ERROR;
      })

      // PROMOTE SITE
      .addCase(loadPromotionSitesAsync.pending, (state) => {
        state.promoteSite.meta.loadStatus = RequestStatus.PENDING;
      })

      .addCase(loadPromotionSitesAsync.fulfilled, (state, action) => {
        state.promoteSite.meta.loadStatus = RequestStatus.SUCCESS;
        state.promoteSite.data.promotedPrograms = action.payload;
      })

      .addCase(loadPromotionSitesAsync.rejected, (state) => {
        state.promoteSite.meta.loadStatus = RequestStatus.ERROR;
      })

      .addCase(postCreatePromotionAsync.pending, (state) => {
        state.promoteSite.meta.createStatus = RequestStatus.PENDING;
      })

      .addCase(postCreatePromotionAsync.fulfilled, (state, action) => {
        state.promoteSite.meta.createStatus = RequestStatus.SUCCESS;
        state.promoteSite.data.selectedProgramId = action.payload;
      })

      .addCase(postCreatePromotionAsync.rejected, (state) => {
        state.promoteSite.meta.createStatus = RequestStatus.ERROR;
      })

      //POSTBACKS
      .addCase(getPostbackListAsync.pending, (state) => {
        state.postback.meta.getPostbacksStatus = RequestStatus.PENDING;
      })

      .addCase(getPostbackListAsync.fulfilled, (state, action) => {
        state.postback.meta.getPostbacksStatus = RequestStatus.SUCCESS;
        state.postback.data.postbacks = action.payload.postbacks;
        state.postback.data.totalPages = action.payload.totalPages;
        state.postback.data.firstLoad = false;
      })

      .addCase(getPostbackListAsync.rejected, (state) => {
        state.postback.meta.getPostbacksStatus = RequestStatus.ERROR;
      })
      .addCase(getPostbackSitesAsync.pending, (state) => {
        state.postback.meta.getSitesStatus = RequestStatus.PENDING;
      })

      .addCase(getPostbackSitesAsync.fulfilled, (state, action) => {
        state.postback.meta.getSitesStatus = RequestStatus.SUCCESS;
        state.postback.data.sites = action.payload.sites;
        state.postback.data.promotions = action.payload.promotions;
      })

      .addCase(getPostbackSitesAsync.rejected, (state) => {
        state.postback.meta.getSitesStatus = RequestStatus.ERROR;
      })

      .addCase(postAddPostbackAsync.pending, (state) => {
        state.postback.meta.addPostbackStatus = RequestStatus.PENDING;
      })

      .addCase(postAddPostbackAsync.fulfilled, (state) => {
        state.postback.meta.addPostbackStatus = RequestStatus.SUCCESS;
      })

      .addCase(postAddPostbackAsync.rejected, (state) => {
        state.postback.meta.addPostbackStatus = RequestStatus.ERROR;
      })

      .addCase(postEditPostbackAsync.pending, (state) => {
        state.postback.meta.editPostbackStatus = RequestStatus.PENDING;
      })

      .addCase(postEditPostbackAsync.fulfilled, (state) => {
        state.postback.meta.editPostbackStatus = RequestStatus.SUCCESS;
      })

      .addCase(postEditPostbackAsync.rejected, (state) => {
        state.postback.meta.editPostbackStatus = RequestStatus.ERROR;
      })

      .addCase(postDeletePostbackAsync.pending, (state) => {
        state.postback.meta.deletePostbackStatus = RequestStatus.PENDING;
      })

      .addCase(postDeletePostbackAsync.fulfilled, (state) => {
        state.postback.meta.deletePostbackStatus = RequestStatus.SUCCESS;
      })

      .addCase(postDeletePostbackAsync.rejected, (state) => {
        state.postback.meta.deletePostbackStatus = RequestStatus.ERROR;
      })

      // BLACK LABEL
      .addCase(postCreateBlackLabelSiteReviewAsync.pending, (state) => {
        const { createBlackLabel } = state;
        createBlackLabel.meta.saveStatus = RequestStatus.PENDING;
      })

      .addCase(postCreateBlackLabelSiteReviewAsync.fulfilled, (state) => {
        const { createBlackLabel } = state;
        createBlackLabel.meta.saveStatus = RequestStatus.SUCCESS;
      })

      .addCase(postCreateBlackLabelSiteReviewAsync.rejected, (state) => {
        const { createBlackLabel } = state;
        createBlackLabel.meta.saveStatus = RequestStatus.ERROR;
      })

      .addCase(getTimezonesAsync.pending, (state) => {
        const { timezones } = state;
        timezones.meta.getStatus = RequestStatus.PENDING;
      })

      .addCase(getTimezonesAsync.fulfilled, (state, action) => {
        const { timezones } = state;
        timezones.meta.getStatus = RequestStatus.SUCCESS;
        timezones.data.result = action.payload;
      });
  },
});

export const MySitesActions = slice.actions;

export const mySitesReducer = slice.reducer;
